<template>
  <div class="product">
    <banner title="产品中心" subtitle="DIGITAL PROJECT MANAGEMENT" />
    <section class="product-matrix" aria-labelledby="product-matrix-title">
      <div class="product-matrix__inner">
        <header class="product-matrix__header" v-reveal>
          <span>PRODUCT PORTFOLIO</span>
          <h2 id="product-matrix-title">从工程协同到门店增长与本地生活</h2>
          <p>面向工程、门店和本地商业场景，提供可落地、可持续运营的数字化产品能力。</p>
        </header>
        <div class="product-matrix__grid">
          <article class="product-entry product-entry--engineering" v-reveal="0">
            <span class="product-entry__state">当前页面</span>
            <h3>工程数字化管理平台</h3>
            <p>围绕工程项目的质量、安全、进度、材料与知识沉淀，连接现场记录、协同处理和过程追溯。</p>
            <a class="product-entry__link" href="#engineering-platform">
              继续了解平台能力
              <i class="el-icon-bottom" aria-hidden="true"></i>
            </a>
          </article>
          <article class="product-entry product-entry--traffic" v-reveal="90">
            <router-link
              class="product-entry__overlay-link"
              to="/traffic-boost"
              aria-label="查看重点产品流量速推详情"
            ></router-link>
            <span class="product-entry__state">重点产品</span>
            <h3>流量速推</h3>
            <p>以长期可用的门店二维码连接到店顾客与商家内容，后台更新素材无需重印，并由顾客确认发布形成传播链路。</p>
            <span class="product-entry__link" aria-hidden="true">
              查看产品详情
              <i class="el-icon-right" aria-hidden="true"></i>
            </span>
          </article>
          <article class="product-entry product-entry--mall" v-reveal="180">
            <router-link
              class="product-entry__overlay-link"
              to="/ganghaolai-mall"
              aria-label="查看刚好来商城产品详情"
            ></router-link>
            <span class="product-entry__state">本地生活商城</span>
            <h3>刚好来商城</h3>
            <p>以自营超市夯实品质供给，连接本地商家与消费者，提供线上选购、到店自提、同城配送和商家经营入口。</p>
            <span class="product-entry__link" aria-hidden="true">
              查看产品详情
              <i class="el-icon-right" aria-hidden="true"></i>
            </span>
          </article>
        </div>
      </div>
    </section>
    <div class="product-content" v-loading="loading">
      <header id="engineering-platform" class="product-intro" v-reveal>
        <span>PLATFORM OVERVIEW</span>
        <h2>让工程管理过程更透明、协同更高效、结果可追溯</h2>
        <p>围绕质量、安全、进度、材料与知识沉淀等关键环节，形成覆盖项目全周期的数字化管理能力。</p>
      </header>

      <!-- 视频区域 -->
      <div class="p-video" aria-label="产品介绍视频" v-reveal>
        <video-player
          class="video-player vjs-custom-skin"
          ref="videoPlayer"
          :playsinline="true"
          :options="playerOptions"
        ></video-player>
      </div>

      <!-- 传统管理模式不足 -->
      <div class="section-block lacking">
        <div class="section-header" v-reveal>
          <h2 class="title">传统管理模式不足</h2>
          <p class="subtitle">SHORTCOMINGS</p>
        </div>
        <div class="lacking-grid" v-reveal="90">
          <div class="lacking-card">
            <div class="lacking-card-icon">
              <img src="../assets/img/product-group-icon_3.png" alt="质量安全检查" />
            </div>
            <p>质量检查、安全检查以抽查为主，难免存在遗漏，留下质量安全隐患。</p>
          </div>
          <div class="lacking-card">
            <div class="lacking-card-icon">
              <img src="../assets/img/product-group-icon_2.png" alt="数据统计分析" />
            </div>
            <p>数据统计、数据分析的缺失，无法为统计、分析、项目评估等工作提供数据支撑。</p>
          </div>
          <div class="lacking-card">
            <div class="lacking-card-icon">
              <img src="../assets/img/product-group-icon_1.png" alt="过程记录追溯" />
            </div>
            <p>检查工作和检查记录文件脱节，无法做到同步对应；缺少影像文档，无法做到可追溯性。</p>
          </div>
        </div>
      </div>

      <!-- 平台目标 -->
      <div class="section-block target">
        <div class="section-header" v-reveal>
          <h2 class="title">平台目标</h2>
          <p class="subtitle">TARGET</p>
        </div>
        <div class="target-img" v-reveal="90">
          <img src="../assets/img/product_group_3_img.png" alt="平台建设目标示意图" />
        </div>
      </div>

      <!-- 功能模块 -->
      <div class="section-block modular">
        <div class="section-header" v-reveal>
          <h2 class="title">功能模块</h2>
          <p class="subtitle">MODULAR</p>
        </div>
        <div class="modular-grid" v-reveal="90">
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_1.png" alt="质量控制模块" />
            </div>
            <p>质量控制模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_2.png" alt="知识仓库模块" />
            </div>
            <p>知识仓库模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_3.png" alt="信息流程模块" />
            </div>
            <p>信息流程模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_4.png" alt="定位复核模块" />
            </div>
            <p>定位复核模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_5.png" alt="信息处理模块" />
            </div>
            <p>信息处理模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_6.png" alt="材料设备模块" />
            </div>
            <p>材料设备模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_7.png" alt="进度控制模块" />
            </div>
            <p>进度控制模块</p>
          </div>
          <div class="module-card">
            <div class="module-card-icon">
              <img src="../assets/img/product-group_4-icon_8.png" alt="安全管理模块" />
            </div>
            <p>安全管理模块</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Banner from "../components/Banner";
import { requestJson } from "../services/siteConfig";
export default {
  data() {
    return {
      loading: true,
      videoSrc: "http://www.shkjem.com/video/kj.mp4",
      playerOptions: {
        playbackRates: [0.5, 1.0, 1.5, 2.0, 4.0, 8.0],
        autoplay: false,
        muted: false,
        loop: false,
        preload: "auto",
        language: "zh-CN",
        aspectRatio: "16:9",
        fluid: true,
        sources: [
          {
            type: "",
            src: "http://www.shkjem.com/video/kj.mp4"
          }
        ],
        notSupportedMessage: "此视频暂无法播放，请稍后再试",
        controlBar: {
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true
        }
      }
    };
  },
  components: {
    Banner
  },
  created() {
    requestJson(
      "/api/DataDictionary/GetDataDictionaryAll?key=%E4%BA%A7%E5%93%81%E8%A7%86%E9%A2%91%E9%93%BE%E6%8E%A5"
    )
      .then(data => {
        const item = Array.isArray(data) ? data[0] : null;
        if (item && item.Content) {
          this.videoSrc = item.Content;
          this.playerOptions.sources[0].src = item.Content;
        }
      })
      .catch(error => {
        window.console.log(error);
      })
      .then(() => {
        this.loading = false;
      });
  }
};
</script>

<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.product {
  width: 100%;
  min-height: 100vh;
  background: #f0f4f8;
}

.product-matrix {
  padding: var(--section-space) 3%;
  background: #fff;
}

.product-matrix__inner {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
}

.product-matrix__header {
  max-width: 720px;
  margin-bottom: 36px;

  span {
    color: var(--brand-600);
    font-size: var(--text-eyebrow);
    font-weight: 700;
    letter-spacing: var(--tracking-eyebrow);
  }

  h2 {
    margin: 12px 0 14px;
    color: var(--ink-950);
    font-size: var(--text-h2);
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: var(--tracking-cjk);
  }

  p {
    color: var(--ink-600);
    font-size: 16px;
    line-height: 1.8;
  }
}

.product-matrix__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.product-entry {
  position: relative;
  min-width: 0;
  padding: 32px;
  border: 1px solid var(--line);
  border-top: 3px solid var(--brand-600);
  border-radius: var(--radius-md);
  background: #fff;
  box-shadow: var(--shadow-xs);
  transition: transform var(--duration-base) var(--ease-out), border-color var(--duration-base) ease, box-shadow var(--duration-base) ease;

  &:hover {
    transform: translateY(var(--hover-lift));
    border-color: rgba(22, 121, 173, 0.35);
    box-shadow: var(--shadow-md);
  }

  h3 {
    margin: 14px 0 12px;
    color: var(--ink-950);
    font-size: 24px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: 0;
    overflow-wrap: anywhere;
  }

  p {
    min-height: 58px;
    color: var(--ink-600);
    font-size: 15px;
    line-height: 1.8;
    overflow-wrap: anywhere;
  }
}

.product-entry--traffic {
  border-color: rgba(17, 117, 107, 0.34);
  border-top-color: var(--tone-traffic);
  background: var(--tone-traffic-soft);
  box-shadow: 0 16px 36px rgba(7, 83, 77, 0.12);
}

.product-entry--traffic:hover {
  border-color: rgba(17, 117, 107, 0.62);
  box-shadow: 0 20px 42px rgba(7, 83, 77, 0.17);
}

.product-entry--traffic:focus-within {
  outline: 3px solid #0d6b62;
  outline-offset: 4px;
}

.product-entry__overlay-link {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: var(--radius-md);
}

.product-entry__overlay-link:focus-visible {
  outline: none;
}

.product-entry__state {
  color: var(--brand-700);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}

.product-entry--traffic .product-entry__state {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid rgba(17, 117, 107, 0.24);
  border-radius: 6px;
  color: var(--tone-traffic-deep);
  background: rgba(255, 255, 255, 0.72);
}

.product-entry__link {
  display: inline-flex;
  margin-top: 24px;
  align-items: center;
  gap: 8px;
  color: var(--brand-700);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: var(--brand-500);
  }

  &:focus-visible {
    outline: 3px solid rgba(31, 145, 199, 0.34);
    outline-offset: 4px;
  }
}

.product-entry--traffic .product-entry__link {
  padding: 9px 13px;
  border-radius: var(--radius-sm);
  color: #fff;
  background: var(--tone-traffic);
}

.product-entry--mall {
  border-color: rgba(215, 120, 36, 0.42);
  border-top-color: var(--tone-merchant);
  background: var(--tone-merchant-soft);
  box-shadow: 0 16px 36px rgba(171, 91, 23, 0.12);
}

.product-entry--mall:hover {
  border-color: rgba(215, 120, 36, 0.72);
  box-shadow: 0 20px 42px rgba(171, 91, 23, 0.18);
}

.product-entry--mall:focus-within {
  outline: 3px solid var(--tone-merchant-deep);
  outline-offset: 4px;
}

.product-entry--mall .product-entry__state {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid rgba(215, 120, 36, 0.34);
  border-radius: 6px;
  color: var(--tone-merchant-deep);
  background: rgba(255, 255, 255, 0.78);
}

.product-entry--mall .product-entry__link {
  padding: 9px 13px;
  border-radius: var(--radius-sm);
  color: #fff;
  background: var(--tone-merchant-strong);
}

.product-entry--mall .product-entry__link:hover {
  color: #fff;
  background: #9f5014;
}

@media (max-width: 1080px) {
  .product-matrix__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.product-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 3% 80px;
}

// 视频区域
.p-video {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 0 60px;
}

// 公共区块样式
.section-block {
  padding: clamp(48px, 6vw, 72px) 0;
}

.section-header {
  text-align: center;
  margin-bottom: 50px;

  .title {
    font-size: var(--text-h2);
    font-weight: 700;
    color: var(--ink-950);
    margin-bottom: 8px;
    letter-spacing: var(--tracking-cjk);
  }

  .subtitle {
    font-size: var(--text-eyebrow);
    font-weight: 700;
    color: var(--ink-400);
    letter-spacing: var(--tracking-eyebrow);
    text-transform: uppercase;
  }
}

// 传统管理模式不足
.lacking-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  .lacking-card {
    background: #fff;
    border-radius: var(--radius-md);
    padding: 48px 32px;
    text-align: center;
    box-shadow: var(--shadow-xs);
    transition: transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) ease;

    &:hover {
      transform: translateY(var(--hover-lift));
      box-shadow: var(--shadow-md);
    }

    &-icon {
      width: 90px;
      height: 90px;
      margin: 0 auto 24px;
      background: linear-gradient(135deg, var(--brand-600) 0%, var(--brand-800) 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      img {
        width: 48px;
        height: 48px;
        object-fit: contain;
      }
    }

    p {
      font-size: 15px;
      color: var(--ink-600);
      line-height: 1.8;
      text-align: left;
    }
  }
}

// 平台目标
.target-img {
  text-align: center;
  img {
    max-width: 100%;
    height: auto;
  }
}

// 功能模块
.modular-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  .module-card {
    background: #fff;
    border-radius: var(--radius-md);
    padding: 36px 20px;
    text-align: center;
    box-shadow: var(--shadow-xs);
    transition: transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) ease;
    cursor: pointer;

    &:hover {
      transform: translateY(var(--hover-lift));
      box-shadow: var(--shadow-md);

      .module-card-icon {
        transform: scale(1.06);
        box-shadow: 0 8px 24px rgba(22, 121, 173, 0.22);
      }
    }

    &-icon {
      width: 72px;
      height: 72px;
      margin: 0 auto 16px;
      background: linear-gradient(135deg, var(--brand-50) 0%, var(--brand-100) 100%);
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) ease;
      overflow: hidden;

      img {
        width: 36px;
        height: 36px;
        object-fit: contain;
      }
    }

    p {
      font-size: 15px;
      font-weight: 600;
      color: var(--ink-950);
      letter-spacing: var(--tracking-cjk);
    }
  }
}

.product-intro {
  max-width: 920px;
  margin: 0 auto;
  padding: clamp(56px, 7vw, 88px) 0 16px;
  scroll-margin-top: 88px;
  text-align: center;

  span {
    color: var(--color-brand);
    font-size: var(--text-eyebrow);
    font-weight: 700;
    letter-spacing: var(--tracking-eyebrow);
  }

  h2 {
    margin: 16px 0 18px;
    color: var(--color-ink);
    font-size: var(--text-h2);
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: var(--tracking-cjk);
  }

  p {
    max-width: 760px;
    margin: 0 auto;
    color: var(--color-ink-muted);
    font-size: 16px;
    line-height: 1.9;
  }
}

.p-video {
  padding-top: 42px;

  ::v-deep .video-js {
    overflow: hidden;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
  }
}

.section-block {
  position: relative;
}

.lacking-card,
.module-card,
.target-img {
  border: 1px solid rgba(15, 50, 79, 0.08);
}

@media (max-width: 1024px) {
  .lacking-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .modular-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .lacking-grid,
  .modular-grid {
    grid-template-columns: 1fr;
  }
  .section-header .title {
    font-size: 24px;
  }
}


@media (max-width: 768px) {
  .product-matrix {
    padding: 52px var(--mobile-gutter);
  }

  .product-matrix__header {
    margin-bottom: 26px;
  }

  .product-matrix__header h2 {
    font-size: 28px;
  }

  .product-matrix__grid {
    grid-template-columns: 1fr;
  }

  .product-entry {
    padding: 26px 22px;
  }

  .product-entry p {
    min-height: 0;
  }

  .product-content {
    width: 100%;
    padding: 0 var(--mobile-gutter) 48px;
  }

  .p-video {
    width: 100%;
    padding: 28px 0 20px;
  }

  .p-video ::v-deep .video-js,
  .p-video ::v-deep .video-js,
  .p-video ::v-deep .vjs-poster,
  .p-video ::v-deep .vjs-poster {
    width: 100%;
    height: auto !important;
    aspect-ratio: 16 / 9;
  }

  .section-block {
    padding: 38px 0;
  }

  .section-header {
    margin-bottom: 26px;
  }

  .section-header .title {
    font-size: 24px;
    line-height: 1.35;
  }

  .lacking-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .lacking-grid .lacking-card {
    padding: 26px 20px;
  }

  .lacking-grid .lacking-card-icon {
    width: 70px;
    height: 70px;
    margin-bottom: 18px;
  }

  .target-img {
    padding: 8px 0;
    overflow-x: auto;
  }

  .target-img img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }

  .modular-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .modular-grid .module-card {
    min-width: 0;
    padding: 22px 10px;
  }

  .modular-grid .module-card-icon {
    width: 58px;
    height: 58px;
    margin-bottom: 12px;
  }

  .modular-grid .module-card p {
    font-size: 14px;
    overflow-wrap: anywhere;
  }
}
</style>
